import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import SEOMeta from '../../components/SEOMeta';
import { useDarkMode } from '../../hooks/useDarkMode';
import { createTrackingEvent, currentTrackingTouches } from '../../lib/tracking/whatsapp';

// ─── Brand tokens ────────────────────────────────────────────────────────────
const C = {
  gold: 'var(--gold)',
  goldLight: 'var(--gold-light)',
  goldPale: 'var(--gold-pale)',
  amberDeep: 'var(--amber-deep)',
  cream: 'var(--cream)',
  warmWhite: 'var(--warm-white)',
  earth: 'var(--earth)',
  earthLight: 'var(--earth-light)',
  terra: 'var(--terra)',
  textBody: 'var(--text-body)',
  textMuted: 'var(--text-muted)',
  beigeMid: 'var(--beige-mid)',
  beigeFooter: 'var(--beige-footer)',
};

// ─── Static data ──────────────────────────────────────────────────────────────
const RECIPIENTS = [
  'Marido', 'Esposa', 'Namorado', 'Namorada', 'Filho(a)',
  'Pai', 'Mãe', 'Irmão(ã)', 'Amigo(a)', 'Para mim',
  'Grupo de pessoas', 'Outro',
];

const GENRES = [
  'Sertanejo', 'MPB', 'Forró', 'Pagode', 'Gospel', 'Pop',
  'Funk', 'Rock', 'Axé', 'Reggae', 'Jovem Guarda', 'Jazz',
  'Bossa Nova', 'Arrocha/Brega', 'Música Clássica', 'Infantil',
  'Música Latina', 'Rap', 'Black Music', 'Música Eletrônica',
  'Bolero', 'Tango', 'Valsa', 'Capoeira', 'Blues Americano',
];

const SUBGENRES: Record<string, string[]> = {
  'Sertanejo': ['Sertanejo Universitário', 'Sertanejo Raiz', 'Sertanejo Romântico'],
  'MPB': ['MPB Clássica', 'MPB Contemporânea', 'MPB Pop'],
  'Forró': ['Forró Pé de Serra', 'Forró Universitário', 'Forró Eletrônico'],
  'Pagode': ['Pagode de Mesa (Raiz)', 'Pagode Romântico (Anos 90)', 'Pagode Universitário / Novo Pagode'],
  'Gospel': ['Gospel de Adoração', 'Gospel Contemporâneo', 'Música de Oração'],
  'Pop': ['Pop Brasileiro', 'Pop Internacional', 'Pop Balada'],
  'Funk': ['Funk Carioca', 'Funk Melody', 'Funk Ostentação'],
  'Rock': ['Rock Nacional', 'Rock Clássico', 'Rock Alternativo'],
  'Axé': ['Axé Baiano', 'Axé Pop', 'Axé Eletrônico'],
  'Reggae': ['Reggae Roots', 'Reggae Maranhense', 'Reggae Pop'],
  'Jovem Guarda': ['Jovem Guarda Clássica', 'Jovem Guarda Romântica'],
  'Jazz': ['Jazz Standards', 'Jazz Brasileiro', 'Smooth Jazz'],
  'Bossa Nova': ['Bossa Nova Clássica', 'Bossa Nova Contemporânea'],
  'Arrocha/Brega': ['Arrocha Romântico', 'Brega Paraense', 'Brega Funk'],
  'Música Clássica': ['Período Barroco', 'Período Clássico', 'Período Romântico'],
  'Infantil': ['Infantil Animado', 'Infantil Acústico', 'Infantil Educativo'],
  'Música Latina': ['Salsa', 'Merengue', 'Bachata'],
  'Rap': ['Rap Consciente', 'Rap Nacional', 'Trap'],
  'Black Music': ['Soul', 'R&B', 'Funk Americano'],
  'Música Eletrônica': ['House', 'Ambient / Lo-fi', 'EDM'],
  'Bolero': ['Bolero Cubano', 'Bolero Mexicano'],
  'Tango': ['Tango Argentino', 'Tango Clássico'],
  'Valsa': ['Valsa Vienense', 'Valsa Brasileira'],
  'Capoeira': ['Capoeira Angola', 'Capoeira Regional'],
  'Blues Americano': ['Blues Clássico', 'Blues Rock', 'Delta Blues'],
};

const STEP_PROGRESS = [17, 33, 50, 67, 83, 100];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function wordCount(text: string): number {
  return text.trim() === '' ? 0 : text.trim().split(/\s+/).length;
}

function resizeImageToJpeg(
  file: File,
  maxDimension: number,
  quality: number,
): Promise<{ base64: string; dataUrl: string }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const ratio = Math.min(1, maxDimension / Math.max(img.width, img.height));
      const w = Math.round(img.width * ratio);
      const h = Math.round(img.height * ratio);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Seu navegador não suporta o processamento da foto.'));
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Não consegui processar essa foto.'));
            return;
          }
          const reader = new FileReader();
          reader.onload = () => {
            const dataUrl = reader.result as string;
            resolve({ base64: dataUrl.split(',')[1] || '', dataUrl });
          };
          reader.onerror = () => reject(new Error('Erro ao ler a foto.'));
          reader.readAsDataURL(blob);
        },
        'image/jpeg',
        quality,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Não foi possível abrir essa foto.'));
    };
    img.src = url;
  });
}

// ─── Reusable tiny components ─────────────────────────────────────────────────
function StepLabel({ current, total }: { current: number; total: number }) {
  return (
    <span style={{ color: C.textMuted, fontSize: 13, fontFamily: "'DM Sans', sans-serif" }}>
      Passo {current} de {total}
    </span>
  );
}

function FieldError({ msg, tone = 'error' }: { msg: string; tone?: 'error' | 'soft' }) {
  const color = tone === 'soft' ? 'var(--amber-deep)' : '#D64C2B';
  return (
    <p style={{ color, fontSize: 13, marginTop: 4, fontFamily: "'DM Sans', sans-serif" }}>
      {msg}
    </p>
  );
}

function WordCounter({ count, min }: { count: number; min?: number }) {
  const ok = min === undefined || count >= min;
  return (
    <span style={{
      fontSize: 12,
      color: ok ? C.textMuted : '#D64C2B',
      fontFamily: "'DM Sans', sans-serif",
    }}>
      {count} {count === 1 ? 'palavra' : 'palavras'}
      {min !== undefined && count < min && ` (mínimo ${min})`}
    </span>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
interface FormData {
  recipient: string;
  recipientName: string;
  genre: string;
  subgenre: string;
  voiceGender: string;
  qualities: string;
  qualitiesGrammar: boolean;
  memories: string;
  memoriesGrammar: boolean;
  heartMessage: string;
  heartMessageGrammar: boolean;
  customerName: string;
  countryCode: string;
  whatsapp: string;
  email: string;
  fotoUrl: string;
  coupon: string;
  couponApplied: boolean;
  couponLoading: boolean;
  couponError: string;
  couponDesconto: number;
  couponValorFinal: number;
}

type Errors = Partial<Record<keyof FormData | 'recipient', string>>;

interface ComposingData {
  ref_id: string;
  checkout_slug: string | null;
}

const N8N_WEBHOOK_URL = 'https://n8n.goautomat.com/webhook/criar-pedido-site-v17';

export default function CriarPage() {
  const isDark = useDarkMode();
  const [searchParams] = useSearchParams();
  const refCoupon = (searchParams.get('ref') || '').trim().toUpperCase();
  const [step, setStep] = useState(1);
  const [savedFlash, setSavedFlash] = useState(false);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState('');
  const [composingData, setComposingData] = useState<ComposingData | null>(null);
  const [form, setForm] = useState<FormData>({
    recipient: '',
    recipientName: '',
    genre: '',
    subgenre: '',
    voiceGender: '',
    qualities: '',
    qualitiesGrammar: true,
    memories: '',
    memoriesGrammar: true,
    heartMessage: '',
    heartMessageGrammar: true,
    customerName: '',
    countryCode: '+55',
    whatsapp: '',
    email: '',
    fotoUrl: '',
    coupon: refCoupon,
    couponApplied: false,
    couponLoading: false,
    couponError: '',
    couponDesconto: 0,
    couponValorFinal: 34.90,
  });
  const [errors, setErrors] = useState<Errors>({});
  const [fotoPreview, setFotoPreview] = useState('');
  const [fotoUploading, setFotoUploading] = useState(false);
  const [fotoError, setFotoError] = useState('');
  const fotoInputRef = useRef<HTMLInputElement>(null);
  const topRef = useRef<HTMLDivElement>(null);

  async function handleFotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!fotoInputRef.current) fotoInputRef.current = e.target;
    e.target.value = '';
    if (!file) return;

    const ALLOWED = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!ALLOWED.includes(file.type)) {
      setFotoError('Formato não suportado. Use JPG, PNG ou WebP.');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setFotoError('Foto muito grande. Máximo 20MB.');
      return;
    }

    setFotoError('');
    setFotoUploading(true);

    try {
      const { base64, dataUrl } = await resizeImageToJpeg(file, 1600, 0.85);
      setFotoPreview(dataUrl);
      const res = await fetch('/api/upload-foto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ foto_base64: base64, mime_type: 'image/jpeg' }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.foto_url) throw new Error(data?.error || 'Erro ao enviar foto');
      set('fotoUrl', data.foto_url);
    } catch (err: unknown) {
      setFotoPreview('');
      set('fotoUrl', '');
      setFotoError(err instanceof Error ? err.message : 'Erro ao enviar foto. Tente novamente.');
    } finally {
      setFotoUploading(false);
    }
  }

  function handleRemoveFoto() {
    setFotoPreview('');
    set('fotoUrl', '');
    setFotoError('');
    if (fotoInputRef.current) fotoInputRef.current.value = '';
  }

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  // ── Redirect pra checkout assim que a prévia ficar pronta ────────────────
  useEffect(() => {
    if (!composingData?.checkout_slug) return;
    let cancelled = false;
    const slug = composingData.checkout_slug;
    const poll = async () => {
      try {
        const r = await fetch(`/api/checkout?action=info&slug=${encodeURIComponent(slug)}&_t=${Date.now()}`, { cache: 'no-store' });
        if (!r.ok) return;
        const data: { status?: string } = await r.json();
        const status = String(data.status || '').toLowerCase();
        if (!cancelled && (status === 'previa_enviada' || status === 'aguardando_pagamento' || status === 'pago' || status === 'gerando_musica' || status === 'entregue')) {
          window.location.href = `/ouvir-musica/${slug}`;
        }
      } catch {
        // falha silenciosa — tenta novamente no próximo ciclo
      }
    };
    poll();
    const interval = setInterval(poll, 6000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [composingData?.checkout_slug]);

  const set = <K extends keyof FormData>(key: K, value: FormData[K]) => {
    setForm(f => ({ ...f, [key]: value }));
    if (errors[key]) setErrors(e => ({ ...e, [key]: undefined }));
  };

  // ── Validation per step ──────────────────────────────────────────────────
  function validate(): boolean {
    const e: Errors = {};
    if (step === 1) {
      if (!form.recipient) e.recipient = 'Selecione para quem é a música.';
      if (!form.recipientName.trim()) e.recipientName = 'Informe o nome do homenageado.';
    }
    if (step === 2) {
      if (!form.genre) e.genre = 'Escolha um estilo musical.';
      if (!form.voiceGender) e.voiceGender = 'Selecione o gênero da voz.';
    }
    if (step === 3) {
      if (!form.qualities.trim())
        e.qualities = 'Conta um pouquinho sobre essa pessoa antes de seguir 💛';
    }
    if (step === 6) {
      if (!form.customerName.trim()) e.customerName = 'Informe seu nome.';
      if (!form.whatsapp.trim()) e.whatsapp = 'Informe o WhatsApp para te avisarmos quando a música estiver pronta.';
      else if (!/^\d{8,15}$/.test(form.whatsapp.replace(/\D/g, '')))
        e.whatsapp = 'Número inválido. Digite apenas os números, sem o código do país.';
      if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
        e.email = 'E-mail inválido.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function handleNext() {
    if (!validate()) return;
    if (step === 3) setSavedFlash(true);
    setStep(s => s + 1);
  }

  function handleBack() {
    setSavedFlash(false);
    setStep(s => s - 1);
  }

  // Cupom não é aplicado aqui — vai direto pra página de checkout (`/ouvir-musica/:slug`)
  // onde cliente escolhe o formato e finaliza o pagamento.

  async function handlePay() {
    if (!validate()) return;
    setPaying(true);
    setPayError('');
    try {
      const detalhes = [
        form.qualities && `Qualidades: ${form.qualities}`,
        form.memories && `Memórias: ${form.memories}`,
        form.heartMessage && `Mensagem do coração: ${form.heartMessage}`,
      ].filter(Boolean).join('\n\n');
      const phoneDigits = `${form.countryCode}${form.whatsapp}`.replace(/\D/g, '');
      const touches = currentTrackingTouches();
      const tracking = await createTrackingEvent({
        eventType: 'site_order_submit',
        phone: phoneDigits,
        email: form.email,
        metadata: {
          source: 'criar_wizard',
          ref_coupon: refCoupon || null,
        },
      }).catch(() => null);

      const body = {
        recipient: form.recipient,
        recipient_name: form.recipientName,
        genre: form.genre,
        subgenre: form.subgenre,
        voice_gender: form.voiceGender,
        qualities: form.qualities,
        memories: form.memories,
        heart_message: form.heartMessage,
        country_code: form.countryCode,
        whatsapp: form.whatsapp.replace(/\D/g, ''),
        email: form.email,
        customer_name: form.customerName,
        detalhes,
        foto_url: form.fotoUrl || undefined,
        tracking_id: tracking?.tracking_id || undefined,
        tracking: {
          first_touch: touches.first_touch,
          last_touch: touches.last_touch,
        },
        product_version: 'simple-v17',
      };

      const res = await fetch(N8N_WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      let data: { ok: boolean; error?: string } & Partial<ComposingData>;
      try {
        data = await res.json();
      } catch {
        throw new Error('Não foi possível processar a resposta do servidor. Tente novamente.');
      }
      if (!res.ok || !data.ok) throw new Error(data.error || 'Erro ao criar pedido');
      setComposingData({
        ref_id: data.ref_id || '',
        checkout_slug: data.checkout_slug || null,
      });
    } catch (err: unknown) {
      setPayError(err instanceof Error ? err.message : 'Erro inesperado. Tente novamente.');
    } finally {
      setPaying(false);
    }
  }

  // ── Dark-mode adaptive tokens ────────────────────────────────────────────
  const pillBg     = isDark ? 'rgba(255,255,255,0.07)' : C.warmWhite;
  const pillBorder = isDark ? 'rgba(200,150,80,0.30)'  : C.beigeMid;
  const inputBg    = isDark ? 'rgba(255,255,255,0.06)' : C.warmWhite;
  const inputBorder= isDark ? 'rgba(200,150,80,0.25)'  : C.beigeMid;
  const cardBg     = isDark ? 'rgba(255,255,255,0.04)' : C.warmWhite;
  const cardBorder = isDark ? 'rgba(255,255,255,0.10)' : C.beigeMid;
  const cardShadow = isDark ? '0 8px 48px rgba(0,0,0,0.50)' : '0 4px 32px rgba(58,38,24,0.08)';

  // ── Shared styles ────────────────────────────────────────────────────────
  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '12px 16px',
    border: `1.5px solid ${inputBorder}`, borderRadius: 12,
    background: inputBg, color: C.textBody,
    fontFamily: "'DM Sans', sans-serif", fontSize: 15,
    outline: 'none', boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  };

  const textareaStyle: React.CSSProperties = {
    ...inputStyle,
    resize: 'vertical', minHeight: 140, lineHeight: 1.6,
  };

  const primaryBtn: React.CSSProperties = {
    background: C.gold, color: '#fff',
    border: 'none', borderRadius: 100,
    padding: '14px 32px', fontSize: 15,
    fontWeight: 600, cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    transition: 'background 0.2s, box-shadow 0.2s',
    boxShadow: '0 2px 16px rgba(200,131,42,0.28)',
    whiteSpace: 'normal',
    textAlign: 'center',
    lineHeight: 1.3,
  };

  const outlineBtn: React.CSSProperties = {
    background: 'transparent', color: C.earth,
    border: `1.5px solid ${C.earth}`, borderRadius: 100,
    padding: '13px 28px', fontSize: 15,
    fontWeight: 500, cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    transition: 'all 0.2s',
    whiteSpace: 'normal',
    textAlign: 'center',
    lineHeight: 1.3,
  };

  const progress = STEP_PROGRESS[step - 1];

  // ─── Step renderers ──────────────────────────────────────────────────────
  // noindex — página transacional, não deve ser indexada pelo Google

  function renderStep1() {
    return (
      <>
        <StepTitle title="Vamos começar pelo básico" sub="Conta pra gente sobre a pessoa especial na sua vida" />

        {/* Recipient chips */}
        <div style={{ marginBottom: 24 }}>
          <label style={labelStyle}>A música é para…</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 8 }}>
            {RECIPIENTS.map(r => (
              <button
                key={r}
                type="button"
                onClick={() => set('recipient', r)}
                style={{
                  padding: '9px 18px', borderRadius: 100,
                  fontSize: 14, fontFamily: "'DM Sans', sans-serif",
                  cursor: 'pointer', transition: 'all 0.15s',
                  fontWeight: form.recipient === r ? 600 : 400,
                  background: form.recipient === r ? C.gold : pillBg,
                  color: form.recipient === r ? '#fff' : C.textBody,
                  border: form.recipient === r ? `1.5px solid ${C.gold}` : `1.5px solid ${pillBorder}`,
                  boxShadow: form.recipient === r ? '0 2px 10px rgba(200,131,42,0.22)' : 'none',
                }}
              >
                {r}
              </button>
            ))}
          </div>
          {errors.recipient && <FieldError msg={errors.recipient} />}
        </div>

        {/* Name field */}
        <div>
          <label style={labelStyle}>Nome do homenageado(a)</label>
          <input
            type="text"
            maxLength={30}
            value={form.recipientName}
            onChange={e => set('recipientName', e.target.value)}
            placeholder="Como ela(e) se chama?"
            style={{
              ...inputStyle,
              borderColor: errors.recipientName ? '#D64C2B' : C.beigeMid,
              marginTop: 8,
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
            {errors.recipientName
              ? <FieldError msg={errors.recipientName} />
              : <span style={{ fontSize: 12, color: C.textMuted, fontFamily: "'DM Sans', sans-serif" }}>
                  Use acentuação correta (ex: Thaís, Jéssica, Luísa)
                </span>
            }
            <span style={{ fontSize: 12, color: C.textMuted, fontFamily: "'DM Sans', sans-serif", flexShrink: 0, marginLeft: 8 }}>
              {form.recipientName.length}/30
            </span>
          </div>
        </div>
      </>
    );
  }

  function renderStep2() {
    const subs = form.genre ? SUBGENRES[form.genre] ?? [] : [];
    return (
      <>
        <StepTitle title="Escolha o estilo" sub="Qual som combina mais com quem vai receber?" />

        {/* Genre chips */}
        <div style={{ marginBottom: 24 }}>
          <label style={labelStyle}>Gênero musical</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
            {GENRES.map(g => (
              <button
                key={g}
                type="button"
                onClick={() => { set('genre', g); set('subgenre', ''); }}
                style={{
                  padding: '8px 16px', borderRadius: 100,
                  fontSize: 13, fontFamily: "'DM Sans', sans-serif",
                  cursor: 'pointer', transition: 'all 0.15s',
                  fontWeight: form.genre === g ? 600 : 400,
                  background: form.genre === g ? C.gold : pillBg,
                  color: form.genre === g ? '#fff' : C.textBody,
                  border: form.genre === g ? `1.5px solid ${C.gold}` : `1.5px solid ${pillBorder}`,
                  boxShadow: form.genre === g ? '0 2px 10px rgba(200,131,42,0.22)' : 'none',
                }}
              >
                {g}
              </button>
            ))}
          </div>
          {errors.genre && <FieldError msg={errors.genre} />}
        </div>

        {/* Subgenre — dynamic */}
        {subs.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>Substilo <span style={{ color: C.textMuted, fontWeight: 400 }}>(opcional)</span></label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
              {subs.map(sg => (
                <button
                  key={sg}
                  type="button"
                  onClick={() => set('subgenre', form.subgenre === sg ? '' : sg)}
                  style={{
                    padding: '8px 16px', borderRadius: 100,
                    fontSize: 13, fontFamily: "'DM Sans', sans-serif",
                    cursor: 'pointer', transition: 'all 0.15s',
                    fontWeight: form.subgenre === sg ? 600 : 400,
                    background: form.subgenre === sg ? C.goldPale : pillBg,
                    color: form.subgenre === sg ? C.amberDeep : C.textBody,
                    border: form.subgenre === sg ? `1.5px solid ${C.goldLight}` : `1.5px solid ${pillBorder}`,
                  }}
                >
                  {sg}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Voice gender */}
        <div>
          <label style={labelStyle}>Gênero da voz</label>
          <p style={{ fontSize: 13, color: C.textMuted, marginBottom: 10, marginTop: 2, fontFamily: "'DM Sans', sans-serif" }}>
            Dica: escolha o mesmo gênero de quem está dando o presente para soar mais pessoal
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {(['Voz Feminina', 'Voz Masculina'] as const).map(v => (
              <button
                key={v}
                type="button"
                onClick={() => set('voiceGender', v)}
                style={{
                  flex: '1 1 160px', padding: '14px 20px',
                  borderRadius: 12, fontSize: 15, fontWeight: form.voiceGender === v ? 600 : 400,
                  fontFamily: "'DM Sans', sans-serif", cursor: 'pointer',
                  background: form.voiceGender === v ? C.goldPale : pillBg,
                  color: form.voiceGender === v ? C.amberDeep : C.textBody,
                  border: form.voiceGender === v ? `2px solid ${C.gold}` : `1.5px solid ${pillBorder}`,
                  transition: 'all 0.15s', textAlign: 'center',
                }}
              >
                {v === 'Voz Feminina' ? '♀ ' : '♂ '}{v}
              </button>
            ))}
          </div>
          {errors.voiceGender && <FieldError msg={errors.voiceGender} />}
        </div>
      </>
    );
  }

  function renderStep3() {
    const wc = wordCount(form.qualities);
    const isMaleRecipient = ['Marido', 'Namorado', 'Pai'].includes(form.recipient);
    const suggestions = isMaleRecipient
      ? ['Ele é carinhoso quando…', 'O que mais admiro nele é…', 'Ele sempre…', 'Tem um jeito único de…']
      : ['Ela é carinhosa quando…', 'O que mais admiro nela é…', 'Ela sempre…', 'Tem um jeito único de…'];
    const appendSuggestion = (s: string) => {
      const current = form.qualities;
      set('qualities', current.trim() ? `${s}\n${current}` : `${s} `);
    };
    return (
      <>
        <StepTitle title="O que faz essa pessoa especial?" sub="Descreva o jeito dela, as qualidades que você mais ama" />

        <div>
          <label style={labelStyle}>Qualidades e personalidade</label>
          <textarea
            value={form.qualities}
            onChange={e => set('qualities', e.target.value)}
            placeholder="Ela(e) é paciente, engraçada(o), encorajadora(or)? O que faz dela(e) alguém incrível pra você?"
            style={{
              ...textareaStyle,
              marginTop: 8,
              borderColor: errors.qualities ? 'var(--gold-light)' : C.beigeMid,
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, flexWrap: 'wrap', gap: 8 }}>
            <WordCounter count={wc} />
          </div>
          {errors.qualities && <FieldError msg={errors.qualities} tone="soft" />}

          {/* Suggestion chips */}
          <div style={{ marginTop: 18 }}>
            <p style={{
              fontSize: 12, color: C.textMuted,
              fontFamily: "'DM Sans', sans-serif",
              marginBottom: 8,
            }}>
              Precisa de uma ajudinha pra começar?
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {suggestions.map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => appendSuggestion(s)}
                  style={{
                    padding: '8px 14px', borderRadius: 100,
                    fontSize: 13, fontFamily: "'DM Sans', sans-serif",
                    cursor: 'pointer', transition: 'all 0.15s',
                    fontWeight: 400,
                    background: pillBg,
                    color: C.textBody,
                    border: `1.5px solid ${pillBorder}`,
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLButtonElement).style.borderColor = C.gold;
                    (e.currentTarget as HTMLButtonElement).style.color = C.amberDeep;
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLButtonElement).style.borderColor = pillBorder;
                    (e.currentTarget as HTMLButtonElement).style.color = C.textBody;
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      </>
    );
  }

  function renderStep4() {
    const wc = wordCount(form.memories);
    return (
      <>
        {savedFlash && (
          <div style={{
            background: 'var(--color-success-bg)', border: '1.5px solid var(--color-success-border)',
            borderRadius: 10, padding: '10px 16px', marginBottom: 20,
            display: 'flex', alignItems: 'center', gap: 8,
            fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: 'var(--color-success-text)',
          }}>
            <span style={{ fontSize: 16 }}>✓</span>
            <strong>SALVO</strong> — qualidades e personalidade registradas com sucesso!
          </div>
        )}

        <StepTitle title="Histórias e memórias de vocês" sub="Quanto mais detalhes, mais especial fica a letra!" />

        <div>
          <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
            Memórias especiais
            <span style={{
              fontSize: 11, padding: '2px 8px', borderRadius: 100,
              background: C.goldPale, color: C.amberDeep,
              fontWeight: 500, fontFamily: "'DM Sans', sans-serif",
            }}>Opcional</span>
          </label>
          <textarea
            value={form.memories}
            onChange={e => set('memories', e.target.value)}
            placeholder="Qual memória você sempre volta? Um momento que vocês riem juntos? Algo que viveram e os aproximou?"
            style={{ ...textareaStyle, marginTop: 8 }}
          />
          <div style={{ marginTop: 6 }}>
            <WordCounter count={wc} />
          </div>
        </div>
      </>
    );
  }

  function renderStep5() {
    const wc = wordCount(form.heartMessage);
    return (
      <>
        <StepTitle title="Uma mensagem do coração" sub="Escreva o que você quer que essa pessoa sinta ao ouvir a música" />

        <div>
          <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
            Mensagem do coração
            <span style={{
              fontSize: 11, padding: '2px 8px', borderRadius: 100,
              background: C.goldPale, color: C.amberDeep,
              fontWeight: 500, fontFamily: "'DM Sans', sans-serif",
            }}>Opcional</span>
          </label>
          <textarea
            value={form.heartMessage}
            onChange={e => set('heartMessage', e.target.value)}
            placeholder="O que você nunca disse o suficiente? Pelo que quer agradecer? Qual é aquela coisa do fundo do coração que quer dizer?"
            style={{ ...textareaStyle, marginTop: 8 }}
          />
          <div style={{ marginTop: 6 }}>
            <WordCounter count={wc} />
          </div>
        </div>
      </>
    );
  }

  function renderStep6() {
    return (
      <>
        <StepTitle
          title="Você está quase lá!"
          sub={`A um passo de presentear ${form.recipientName || 'alguém especial'} com uma música que nunca vai esquecer`}
        />

        {/* Order summary card */}
        <div style={{
          background: C.goldPale, borderRadius: 14,
          padding: '18px 20px', marginBottom: 28,
          border: `1px solid ${C.goldLight}`,
        }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: C.amberDeep, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12, fontFamily: "'DM Sans', sans-serif" }}>
            Resumo do pedido
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <SummaryRow icon="🎵" label="Para" value={form.recipient ? `${form.recipient}${form.recipientName ? ` — ${form.recipientName}` : ''}` : '—'} />
            <SummaryRow icon="🎸" label="Estilo" value={form.genre ? `${form.genre}${form.subgenre ? ` · ${form.subgenre}` : ''}` : '—'} />
            <SummaryRow icon="🎤" label="Voz" value={form.voiceGender || '—'} />
            <SummaryRow icon="⏱" label="Prévias" value="Em até 3 minutos no seu WhatsApp" />
          </div>
        </div>

        {/* Customer name */}
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>
            Como você se chama? <span style={{ color: '#D64C2B' }}>*</span>
          </label>
          <input
            type="text"
            value={form.customerName}
            onChange={e => set('customerName', e.target.value)}
            placeholder="Seu nome"
            maxLength={80}
            style={{
              ...inputStyle,
              marginTop: 8,
              borderColor: errors.customerName ? '#D64C2B' : C.beigeMid,
            }}
          />
          {errors.customerName && <FieldError msg={errors.customerName} />}
        </div>

        {/* WhatsApp */}
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>
            WhatsApp <span style={{ color: '#D64C2B' }}>*</span>
          </label>
          <p style={{ fontSize: 13, color: C.textMuted, marginBottom: 8, marginTop: 2, fontFamily: "'DM Sans', sans-serif" }}>
            Pra te avisar quando a música estiver pronta 🎵
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <span
              style={{
                padding: '12px 14px', borderRadius: 12,
                border: `1.5px solid ${inputBorder}`, background: inputBg,
                color: C.textBody, fontFamily: "'DM Sans', sans-serif",
                fontSize: 14, fontWeight: 500, flexShrink: 0,
                display: 'inline-flex', alignItems: 'center', gap: 6,
                userSelect: 'none',
              }}
              aria-label="Brasil +55"
            >
              🇧🇷 +55
            </span>
            <input
              type="tel"
              value={form.whatsapp}
              onChange={e => {
                const digits = e.target.value.replace(/\D/g, '').slice(0, 11);
                let fmt = digits;
                if (digits.length > 10) fmt = `(${digits.slice(0,2)}) ${digits.slice(2,7)}-${digits.slice(7)}`;
                else if (digits.length > 6) fmt = `(${digits.slice(0,2)}) ${digits.slice(2,6)}-${digits.slice(6)}`;
                else if (digits.length > 2) fmt = `(${digits.slice(0,2)}) ${digits.slice(2)}`;
                else if (digits.length > 0) fmt = `(${digits}`;
                set('whatsapp', fmt);
              }}
              placeholder="(11) 98765-4321"
              maxLength={15}
              style={{
                ...inputStyle,
                borderColor: errors.whatsapp ? '#D64C2B' : C.beigeMid,
              }}
            />
          </div>
          {errors.whatsapp && <FieldError msg={errors.whatsapp} />}
        </div>

        {/* Email (optional) */}
        <div style={{ marginBottom: 24 }}>
          <label style={labelStyle}>
            E-mail <span style={{ fontSize: 12, fontWeight: 400, color: C.textMuted }}>(opcional)</span>
          </label>
          <p style={{ fontSize: 13, color: C.textMuted, marginBottom: 8, marginTop: 2, fontFamily: "'DM Sans', sans-serif" }}>
            Pode deixar em branco — o WhatsApp é o canal principal de comunicação
          </p>
          <input
            type="email"
            value={form.email}
            onChange={e => set('email', e.target.value)}
            placeholder="seu@email.com.br"
            style={{
              ...inputStyle,
              borderColor: errors.email ? '#D64C2B' : C.beigeMid,
            }}
          />
          {errors.email && <FieldError msg={errors.email} />}
        </div>

        {/* Foto opcional */}
        <div style={{ marginBottom: 24 }}>
          <label style={labelStyle}>
            📷 Adicionar foto da capa{' '}
            <span style={{ fontSize: 12, fontWeight: 400, color: C.textMuted }}>(opcional)</span>
          </label>
          <p style={{ fontSize: 13, color: C.textMuted, marginTop: 2, marginBottom: 10, fontFamily: "'DM Sans', sans-serif" }}>
            Aparece no player da música. JPG, PNG ou WebP
          </p>
          <input
            ref={fotoInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            style={{ display: 'none' }}
            onChange={handleFotoChange}
          />
          {!fotoPreview ? (
            <button
              type="button"
              onClick={() => fotoInputRef.current?.click()}
              disabled={fotoUploading}
              style={{
                width: '100%', padding: '20px 16px',
                border: `2px dashed ${C.beigeMid}`, borderRadius: 14,
                background: 'transparent', cursor: fotoUploading ? 'wait' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                color: C.textMuted, fontFamily: "'DM Sans', sans-serif", fontSize: 14,
                transition: 'border-color 0.2s',
              }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = C.gold)}
              onMouseLeave={e => (e.currentTarget.style.borderColor = C.beigeMid)}
            >
              {fotoUploading ? '⏳ Enviando...' : '📷 Escolher foto'}
            </button>
          ) : (
            <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
              <img
                src={fotoPreview}
                alt="Prévia da foto"
                style={{
                  width: '100%', maxHeight: 200, objectFit: 'cover',
                  borderRadius: 14, border: `2px solid ${C.gold}`,
                  display: 'block',
                }}
              />
              {fotoUploading && (
                <div style={{
                  position: 'absolute', inset: 0, borderRadius: 14,
                  background: 'rgba(0,0,0,0.4)', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontFamily: "'DM Sans', sans-serif", fontSize: 14,
                }}>
                  ⏳ Enviando...
                </div>
              )}
              {!fotoUploading && form.fotoUrl && (
                <div style={{
                  position: 'absolute', top: 8, right: 8,
                  background: 'rgba(0,0,0,0.55)', borderRadius: 8,
                  padding: '2px 8px', color: '#fff', fontSize: 12,
                  fontFamily: "'DM Sans', sans-serif",
                }}>
                  ✅ Enviada
                </div>
              )}
              <button
                type="button"
                onClick={handleRemoveFoto}
                style={{
                  position: 'absolute', bottom: 8, right: 8,
                  background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: 8,
                  color: '#fff', cursor: 'pointer', padding: '4px 10px',
                  fontFamily: "'DM Sans', sans-serif", fontSize: 12,
                }}
              >
                Remover
              </button>
            </div>
          )}
          {fotoError && (
            <p style={{ fontSize: 13, color: '#B91C1C', marginTop: 6, fontFamily: "'DM Sans', sans-serif" }}>
              ⚠️ {fotoError}
            </p>
          )}
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: C.beigeMid, margin: '4px 0 20px' }} />

        {/* Bloco explicativo do fluxo de prévias */}
        <div style={{
          background: C.goldPale, border: `1px solid ${C.goldLight}`,
          borderRadius: 14, padding: '16px 18px', marginBottom: 20,
          fontFamily: "'DM Sans', sans-serif",
        }}>
          <p style={{ fontSize: 14, fontWeight: 700, color: C.amberDeep, margin: '0 0 6px', letterSpacing: '-0.01em' }}>
            🎵 Como funciona daqui pra frente
          </p>
          <p style={{ fontSize: 13.5, color: C.textBody, margin: 0, lineHeight: 1.55 }}>
            A gente compõe <strong>uma prévia da sua música</strong>. Você ouve, aprova ou pede uma alteração e então
            escolhe entre a música em áudio <strong>(R$ 34,90)</strong> ou a música com vídeo usando suas fotos
            <strong> (R$ 49,90)</strong>. Os dois ficam prontos em até 30 minutos.
          </p>
        </div>

        {/* CTA */}
        {payError && (
          <div style={{
            background: 'var(--color-error-bg)', border: '1px solid var(--color-error-border)',
            borderRadius: 10, padding: '12px 16px', marginBottom: 16,
            fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: '#B91C1C',
          }}>
            ⚠️ {payError}
          </div>
        )}
        <button
          type="button"
          disabled={paying}
          onClick={handlePay}
          style={{
            ...primaryBtn,
            width: '100%', fontSize: 18, padding: '18px 32px',
            borderRadius: 100, textAlign: 'center',
            boxShadow: '0 4px 24px rgba(200,131,42,0.35)',
            opacity: paying ? 0.75 : 1,
            cursor: paying ? 'wait' : 'pointer',
          }}
          onMouseEnter={e => { if (!paying) (e.currentTarget as HTMLButtonElement).style.background = C.amberDeep; }}
          onMouseLeave={e => { if (!paying) (e.currentTarget as HTMLButtonElement).style.background = C.gold; }}
        >
          {paying ? '⏳ Compondo sua prévia...' : '🎵 Compor minha prévia grátis'}
        </button>

        <p style={{ fontSize: 12, color: C.textMuted, textAlign: 'center', marginTop: 10, fontFamily: "'DM Sans', sans-serif" }}>
          Sem custo nessa etapa — você só paga depois de ouvir.{' '}
          <a href="/termos-de-servico" style={{ color: C.gold, textDecoration: 'underline' }}>Termos</a> e{' '}
          <a href="/politica-de-privacidade" style={{ color: C.gold, textDecoration: 'underline' }}>Privacidade</a>.
        </p>

        {/* Trust badges */}
        <div style={{
          display: 'flex', justifyContent: 'center', flexWrap: 'wrap',
          gap: 20, marginTop: 28, paddingTop: 20,
          borderTop: `1px solid ${C.beigeMid}`,
        }}>
          {[
            { icon: '🔒', label: 'Pagamento seguro' },
            { icon: '⏱', label: 'Entrega em até 30 min' },
            { icon: '🎵', label: 'Música exclusiva' },
            { icon: '💯', label: 'Garantia 100%' },
          ].map(b => (
            <div key={b.label} style={{ textAlign: 'center', minWidth: 80 }}>
              <div style={{ fontSize: 22, marginBottom: 4 }}>{b.icon}</div>
              <p style={{ fontSize: 12, color: C.textMuted, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.3 }}>{b.label}</p>
            </div>
          ))}
        </div>

        <div style={{
          marginTop: 20, padding: '14px 18px',
          background: 'var(--color-success-bg)', border: '1px solid var(--color-success-border)',
          borderRadius: 10, textAlign: 'center',
          fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: 'var(--color-success-text)',
        }}>
          💚 Garantia de 100% de satisfação — refazemos ou devolvemos seu dinheiro, sem perguntas
        </div>
      </>
    );
  }

  // ─── Next button label ───────────────────────────────────────────────────
  function nextLabel() {
    if (step === 4 || step === 5) return 'Próximo, já falei tudo que queria';
    if (step === 6) return null; // CTA inline
    return 'Próximo';
  }

  const labelStyle: React.CSSProperties = {
    fontSize: 14,
    fontWeight: 600,
    color: C.earthLight,
    fontFamily: "'DM Sans', sans-serif",
    display: 'block',
  };

  // ─── Tela de "compondo prévia" — auto-redireciona pra /ouvir-musica/<slug> ─
  if (composingData) {
    const checkoutUrl = composingData.checkout_slug
      ? `/ouvir-musica/${composingData.checkout_slug}`
      : null;

    return (
      <div style={{ minHeight: '100vh', background: 'transparent', fontFamily: "'DM Sans', sans-serif" }}>
        <header
          className="glass"
          style={{
            background: isDark ? 'rgba(10,5,20,0.65)' : C.warmWhite,
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : C.beigeMid}`,
            padding: '0 24px', height: 120, display: 'flex', alignItems: 'center',
            position: 'sticky', top: 0, zIndex: 50,
            boxShadow: isDark ? '0 1px 20px rgba(0,0,0,0.40)' : '0 1px 12px rgba(58,38,24,0.06)',
          }}
        >
          <a href="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
            <img src={isDark ? '/logo-dark.png' : '/logo-light.png'} alt="Inspira Músicas" style={{ height: 52, objectFit: 'contain' }} />
          </a>
        </header>

        <main style={{ maxWidth: 520, margin: '0 auto', padding: '40px 20px 60px' }}>
          <div
            className="glass"
            style={{
              background: cardBg, borderRadius: 20,
              boxShadow: cardShadow,
              padding: '36px 28px', border: `1px solid ${cardBorder}`,
              textAlign: 'center',
            }}
          >
            <style>{`
              @keyframes inspiraSpin { to { transform: rotate(360deg); } }
              @keyframes inspiraPulse { 0%, 100% { opacity: 0.55; } 50% { opacity: 1; } }
            `}</style>

            <div
              aria-hidden
              style={{
                width: 56, height: 56, margin: '0 auto 22px',
                border: `3px solid ${C.beigeMid}`,
                borderTopColor: C.gold,
                borderRadius: '50%',
                animation: 'inspiraSpin 0.9s linear infinite',
              }}
            />

            <h1 style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 'clamp(22px,5vw,28px)', color: 'var(--heading-ink)',
              fontWeight: 800, letterSpacing: '-0.03em', margin: '0 0 10px',
            }}>
              Compondo a música pra <span style={{ color: C.gold }}>{form.recipientName || 'sua pessoa'}</span>
            </h1>
            <p style={{ fontSize: 15, color: C.textMuted, marginBottom: 24, lineHeight: 1.55 }}>
              Você recebe um link no seu WhatsApp para ouvir a prévia. Se aprovar, escolhe o pacote e finaliza o pagamento por PIX, cartão ou boleto; se quiser mudar algo, é só enviar o feedback.
            </p>

            <div style={{
              background: C.goldPale, border: `1px solid ${C.goldLight}`,
              borderRadius: 14, padding: '16px 18px', marginBottom: 22,
              textAlign: 'left',
            }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: C.amberDeep, margin: '0 0 6px', letterSpacing: '0.03em', textTransform: 'uppercase' }}>
                Próximos passos
              </p>
              <ol style={{ fontSize: 13.5, color: C.textBody, margin: 0, paddingLeft: 20, lineHeight: 1.65 }}>
                <li>Aguarde o WhatsApp da Inspira (<strong>+55 11 92679-7492</strong>)</li>
                <li>Toque no link, ouça a prévia e aprove ou peça uma alteração</li>
                <li>Pague pela forma que preferir e receba a música completa</li>
              </ol>
            </div>

            {checkoutUrl && (
              <a
                href={checkoutUrl}
                style={{
                  display: 'inline-block',
                  padding: '14px 26px',
                  background: 'transparent',
                  color: C.earth,
                  border: `1.5px solid ${C.earth}`,
                  borderRadius: 100,
                  fontWeight: 600, fontSize: 14,
                  fontFamily: "'DM Sans', sans-serif",
                  textDecoration: 'none',
                }}
              >
                Já estão prontas? Abrir agora →
              </a>
            )}

            <div style={{
              marginTop: 24, padding: '14px 18px',
              background: 'var(--color-success-bg)', border: '1px solid var(--color-success-border)',
              borderRadius: 12, textAlign: 'left',
              fontSize: 13, color: 'var(--color-success-text)', lineHeight: 1.55,
            }}>
              💚 <strong>Sem custo nessa etapa.</strong> Você só paga depois de ouvir as 2 versões e escolher uma.
            </div>
          </div>

          <p style={{
            marginTop: 20, textAlign: 'center',
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 11, color: 'var(--text-soft)',
            lineHeight: 1.5,
          }}>
            INSPIRA MÚSICAS · AUTOMAT TECNOLOGIA DA INFORMACAO LTDA · CNPJ 59.640.197/0001-76
            <br />
            Rua Pais Leme, 215 · Pinheiros · São Paulo/SP
          </p>
        </main>
      </div>
    );
  }

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <>
      <SEOMeta
        title="Criar Música Personalizada | Inspira Músicas"
        description="Personalize sua música em minutos."
        path="/criar"
        noIndex
      />
    <div style={{ minHeight: '100vh', background: 'transparent', fontFamily: "'DM Sans', sans-serif" }}>
      {/* Mini header */}
      <header
        className="glass"
        style={{
          background: isDark ? 'rgba(10,5,20,0.65)' : C.warmWhite,
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : C.beigeMid}`,
          padding: '0 24px', height: 120,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, zIndex: 50,
          boxShadow: isDark ? '0 1px 20px rgba(0,0,0,0.40)' : '0 1px 12px rgba(58,38,24,0.06)',
        }}
      >
        <a href="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
          <img src={isDark ? '/logo-dark.png' : '/logo-light.png'} alt="Inspira Músicas" style={{ height: 52, objectFit: 'contain' }} />
        </a>
        <div style={{ textAlign: 'right' }}>
          <StepLabel current={step} total={6} />
          <p style={{ fontSize: 12, color: C.gold, fontWeight: 600, fontFamily: "'DM Sans', sans-serif", marginTop: 1 }}>
            {progress}% concluído
          </p>
        </div>
      </header>

      {/* Price seal (visible across all 6 steps) */}
      <div style={{
        background: C.goldPale,
        borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : C.beigeMid}`,
        padding: '8px 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: 8, flexWrap: 'wrap',
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 12.5, color: C.amberDeep, fontWeight: 500,
        lineHeight: 1.4,
      }}>
        <span>🎵 Música personalizada</span>
        <span style={{ opacity: 0.5 }}>·</span>
        <strong>a partir de R$ 34,90</strong>
        <span style={{ opacity: 0.5 }}>·</span>
        <span>PIX, cartão ou boleto</span>
      </div>

      {/* Progress bar */}
      <div ref={topRef} style={{ background: C.beigeMid, height: 6 }}>
        <div style={{
          height: '100%',
          width: `${progress}%`,
          background: `linear-gradient(90deg, ${C.gold}, ${C.goldLight})`,
          borderRadius: '0 3px 3px 0',
          transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: `0 0 8px rgba(200,131,42,0.4)`,
        }} />
      </div>

      {/* Main content */}
      <main style={{
        maxWidth: 620, margin: '0 auto',
        padding: '40px 20px 60px',
      }}>
        {refCoupon && (
          <div
            className="glass"
            style={{
              background: C.goldPale,
              border: `1px solid ${C.goldLight}`,
              borderRadius: 14,
              padding: '12px 16px',
              marginBottom: 16,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            <span style={{ fontSize: 22, lineHeight: 1 }}>🎁</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.amberDeep, letterSpacing: '0.02em' }}>
                Cupom de desconto disponível
              </p>
              <p style={{ margin: '2px 0 0', fontSize: 13, color: C.textBody, lineHeight: 1.4 }}>
                Aplique <strong style={{ color: C.amberDeep, letterSpacing: '0.04em' }}>{refCoupon}</strong> no último passo para garantir o desconto.
              </p>
            </div>
          </div>
        )}
        <div
          className="glass"
          style={{
            background: cardBg, borderRadius: 20,
            boxShadow: cardShadow,
            padding: '32px 28px',
            border: `1px solid ${cardBorder}`,
          }}
        >
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
          {step === 5 && renderStep5()}
          {step === 6 && renderStep6()}

          {/* Navigation */}
          {step < 6 && (
            <div style={{
              display: 'flex', justifyContent: step === 1 ? 'flex-end' : 'space-between',
              alignItems: 'center', marginTop: 32, gap: 12, flexWrap: 'wrap-reverse'
            }}>
              {step > 1 && (
                <button
                  type="button"
                  onClick={handleBack}
                  style={outlineBtn}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLButtonElement).style.background = C.earth;
                    (e.currentTarget as HTMLButtonElement).style.color = '#fff';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                    (e.currentTarget as HTMLButtonElement).style.color = C.earth;
                  }}
                >
                  ← Voltar
                </button>
              )}
              <button
                type="button"
                onClick={handleNext}
                style={primaryBtn}
                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = C.amberDeep; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = C.gold; }}
              >
                {nextLabel()} →
              </button>
            </div>
          )}

          {step === 6 && step > 1 && (
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <button
                type="button"
                onClick={handleBack}
                style={{ ...outlineBtn, fontSize: 14, padding: '10px 22px' }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLButtonElement).style.background = C.earth;
                  (e.currentTarget as HTMLButtonElement).style.color = '#fff';
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                  (e.currentTarget as HTMLButtonElement).style.color = C.earth;
                }}
              >
                ← Revisar respostas anteriores
              </button>
            </div>
          )}
        </div>

        {/* Step dots */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 24 }}>
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} style={{
              width: i + 1 === step ? 24 : 8, height: 8, borderRadius: 4,
              background: i + 1 <= step ? C.gold : C.beigeMid,
              transition: 'all 0.3s',
            }} />
          ))}
        </div>
      </main>
    </div>
    </>
  );
}

// ─── Small helpers rendered inside steps ──────────────────────────────────────
function StepTitle({ title, sub }: { title: string; sub: string }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <h1 style={{
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 'clamp(22px, 5vw, 28px)',
        color: 'var(--heading-ink)',
        fontWeight: 800, letterSpacing: '-0.03em', margin: 0, lineHeight: 1.25,
      }}>
        {title}
      </h1>
      <p style={{
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 15, color: 'var(--text-muted)',
        marginTop: 8, lineHeight: 1.5,
      }}>
        {sub}
      </p>
    </div>
  );
}

function SummaryRow({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontFamily: "'DM Sans', sans-serif" }}>
      <span style={{ fontSize: 14 }}>{icon}</span>
      <span style={{ fontSize: 13, color: 'var(--text-muted)', minWidth: 50 }}>{label}:</span>
      <span style={{ fontSize: 14, color: 'var(--text-body)', fontWeight: 500 }}>{value}</span>
    </div>
  );
}
